"use client";

import * as React from "react";
import type { CellState, CellValue, TableColumn } from "@quovy/types";
import { parseCitations } from "@quovy/field-types";
import { cn } from "../../../utils";
import { CellEmpty } from "./cell-empty";
import { CellError } from "./cell-error";
import { CellLoading } from "./cell-loading";
import { CellNotFound } from "./cell-not-found";
import { CellStatusBadge } from "./cell-status-badge";
import { CitationBadge } from "./citation-badge";
import { ValidationIndicator } from "./validation-indicator";
import { ValueDisplay } from "../../value-display";
import { InlineEdit } from "./inline-edit";
import { CellMenu, type CellMenuActions } from "./cell-menu";
import { deriveCellOverlays, deriveCellView, type CellView } from "./cell-view";

export interface EditableCellProps {
  field: TableColumn;
  cell: CellState;
  rowId: string;
  selected?: boolean;
  focused?: boolean;
  editing?: boolean;
  onStartEdit?: () => void;
  onStopEdit?: () => void;
  onCommitEdit?: (next: CellValue) => void | Promise<void>;
  /**
   * Called when the user picks one of the citation rows shown inside the
   * inline-edit popover. When provided, the popover delegates the click
   * entirely to this handler (no default snippet-commit). Used by surfaces
   * like the submission locations editor that want to translate a picked
   * citation (which they synthesize from extracted assertions) into a
   * value + linked `assertion_id` write rather than committing the
   * snippet verbatim.
   */
  onCitationSelect?: (citationId: string) => void;
  /**
   * Called when the user clicks the eye icon on a citation row inside the
   * inline-edit popover. Unlike `onCitationSelect`, this opens a read-only
   * preview of the cited source and does NOT commit or close the editor.
   */
  onCitationView?: (citationId: string) => void;
  onOpenFileViewer?: (
    rowId: string,
    opts?: { columnId?: string; initialTab?: "citations" | "history" | "validation" },
  ) => void;
  /** Wire up the context-menu actions. When undefined, the menu still renders standard items as no-ops. */
  contextActions?: CellMenuActions;
  /** When true, render the in-cell citation affordance next to the value (if citations exist). */
  showCitationBadge?: boolean;
  className?: string;
}

function pickValueRenderer(
  field: TableColumn,
  value: CellValue,
  onOpenFileViewer: EditableCellProps["onOpenFileViewer"],
  rowId: string,
): React.ReactNode {
  // Build per-type options. `document` is the only built-in that threads a
  // cell-level interaction (open the file viewer) through options.
  // `enum` / `multi-value` carry `variantMap` once `TableColumn` exposes it
  // — pass it through if present on the field schema.
  let options: unknown;
  if (field.valueType === "document") {
    const onClick = onOpenFileViewer
      ? () => onOpenFileViewer(rowId, { columnId: field.id })
      : undefined;
    if (onClick) options = { onClick };
  } else if (field.valueType === "enum" || field.valueType === "multi-value") {
    const variantMap = (field as { variantMap?: Record<string, string> }).variantMap;
    if (variantMap) options = { variantMap };
  }

  return (
    <ValueDisplay
      type={field.valueType}
      value={value}
      {...(options !== undefined ? { options } : {})}
    />
  );
}

function EditableCellInner({
  field,
  cell,
  rowId,
  selected,
  focused,
  editing,
  onStartEdit,
  onStopEdit,
  onCommitEdit,
  onCitationSelect,
  onCitationView,
  onOpenFileViewer,
  contextActions,
  showCitationBadge,
  className,
}: EditableCellProps) {
  const isEditingView = !!editing && field.editable !== false;
  const view: CellView = deriveCellView(cell, isEditingView);
  const overlays = deriveCellOverlays(cell);

  const parsedCitations = React.useMemo(() => parseCitations(cell.citations), [cell.citations]);

  let body: React.ReactNode;
  switch (view.kind) {
    case "loading":
      body = <CellLoading label={view.status} />;
      break;
    case "error":
      body = (
        <CellError
          errorMessage={view.errorMessage}
          {...(view.attempt !== undefined ? { attempt: view.attempt } : {})}
        />
      );
      break;
    case "not-found":
      body = <CellNotFound />;
      break;
    case "empty":
      body = <CellEmpty />;
      break;
    case "value": {
      const rendered = pickValueRenderer(field, view.value, onOpenFileViewer, rowId);
      body = rendered == null ? <CellEmpty /> : rendered;
      break;
    }
    case "editing": {
      // Render the underlying value (or empty placeholder) inside the
      // inline-edit wrapper so the popover still has a trigger surface.
      const rendered = pickValueRenderer(field, view.value, onOpenFileViewer, rowId);
      const inner = rendered == null ? <CellEmpty /> : rendered;
      const editorOptions =
        (field.valueType === "enum" || field.valueType === "multi-value") &&
        Array.isArray(field.options)
          ? { options: field.options }
          : undefined;
      body = (
        <InlineEdit
          fieldType={field.valueType}
          value={view.value}
          {...(editorOptions !== undefined ? { options: editorOptions } : {})}
          citations={parsedCitations}
          editing={true}
          onEditingChange={(next) => {
            if (!next) onStopEdit?.();
          }}
          onCommit={async (next) => {
            await onCommitEdit?.(next);
            onStopEdit?.();
          }}
          onCancel={onStopEdit ?? (() => {})}
          {...(onCitationSelect
            ? {
                onCitationSelect: (citationId: string) => {
                  onCitationSelect(citationId);
                  onStopEdit?.();
                },
              }
            : {})}
          {...(onCitationView ? { onCitationView } : {})}
        >
          {inner}
        </InlineEdit>
      );
      break;
    }
  }

  const isClickable = !!onStartEdit && field.editable !== false && !editing;
  const handleClick = React.useCallback(() => {
    if (isClickable) onStartEdit?.();
  }, [isClickable, onStartEdit]);
  const handleKeyDown = React.useCallback(
    (e: React.KeyboardEvent) => {
      if (!isClickable) return;
      if (e.key === "Enter") {
        e.preventDefault();
        onStartEdit?.();
      }
    },
    [isClickable, onStartEdit],
  );
  const handleOpenCitationsInDrawer = React.useCallback(() => {
    onOpenFileViewer?.(rowId, { columnId: field.id, initialTab: "citations" });
  }, [onOpenFileViewer, rowId, field.id]);

  const inner = (
    <div
      role={isClickable ? "button" : undefined}
      tabIndex={focused ? 0 : -1}
      onClick={isClickable ? handleClick : undefined}
      onKeyDown={isClickable ? handleKeyDown : undefined}
      data-selected={selected ? "true" : undefined}
      data-focused={focused ? "true" : undefined}
      className={cn(
        "group/cell @container relative flex w-full min-w-0 flex-1 items-center gap-1.5 px-3 py-1.5 text-sm",
        focused && "ring-1 ring-primary/40 ring-inset",
        selected && "bg-primary/5",
        className,
      )}
    >
      <div className="flex min-w-0 flex-1 items-center overflow-hidden">{body}</div>
      <div className="flex shrink-0 items-center gap-1">
        {overlays.badge && <CellStatusBadge kind={overlays.badge} />}
        <ValidationIndicator results={cell.validation} />
        {showCitationBadge && cell.citations.length > 0 && (
          <CitationBadge
            citations={parsedCitations}
            {...(onOpenFileViewer ? { onOpenInDrawer: handleOpenCitationsInDrawer } : {})}
          />
        )}
      </div>
    </div>
  );

  return <CellMenu {...(contextActions ?? {})}>{inner}</CellMenu>;
}

// Handler identity is intentionally ignored — callers must pass stable refs.
function editableCellPropsEqual(prev: EditableCellProps, next: EditableCellProps): boolean {
  return (
    prev.cell === next.cell &&
    prev.field === next.field &&
    prev.rowId === next.rowId &&
    prev.selected === next.selected &&
    prev.focused === next.focused &&
    prev.editing === next.editing &&
    prev.showCitationBadge === next.showCitationBadge &&
    prev.className === next.className
  );
}

export const EditableCell = React.memo(EditableCellInner, editableCellPropsEqual);
